// src/components/loads/hooks/useDropdownData.js
import { useState, useEffect } from 'react';
import { db } from '../../../firebase';
import { collection, query, where, onSnapshot, orderBy, doc, getDoc, setDoc, serverTimestamp } from "firebase/firestore";
import { COMMODITY_TYPES } from '../utils/constants';
import { isVisibleToCompanies, isDriverVisibleTo, getViewerScope, limitToLinkedDrivers } from '../../../utils/companyScope';
import { rememberPeople } from '../../../utils/loadCreator';
import { getUserTeamIds } from '../../../utils/teams';

// ============================================================================
// HELPER: Check if user is Super Admin
// ============================================================================
const rolesOf = (user) =>
  Array.isArray(user?.role) ? user.role : [user?.role].filter(Boolean);

const isSuperAdmin = (user) => rolesOf(user).includes('Super Admin');



/**
 * Custom hook to manage all dropdown data for loads
 * PHASE 2: Non-Super Admin users only see drivers/trucks/dispatchers 
 * assigned to their parent companies
 * @param {Object} loggedInUser - Current logged in user
 * @returns {Object} All dropdown data and loading states
 */
export const useDropdownData = (loggedInUser) => {
  const [drivers, setDrivers] = useState([]);
  // Every driver in the tenant, unscoped. Used only so a load's already-assigned
  // driver can still be shown when they're inactive or in another company —
  // never as the list you pick a NEW driver from.
  const [allDrivers, setAllDrivers] = useState([]);
  const [trucks, setTrucks] = useState([]);
  const [dispatchers, setDispatchers] = useState([]);
  // Every user in the tenant with their teams, unfiltered. Not for pickers —
  // only to work out which team a load belongs to (utils/loadTeam), which
  // needs the teams of dispatchers this viewer is not allowed to pick.
  const [teamUsers, setTeamUsers] = useState([]);
  // Every active dispatcher in the tenant, before the viewer narrowing below.
  // The load row picks from these by the DRIVER's team (utils/loadDispatchers),
  // so the list a row offers depends on the load, not on who is looking.
  const [allDispatchers, setAllDispatchers] = useState([]);
  const [brokers, setBrokers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [commodityTypes, setCommodityTypes] = useState([]);
  const [isLoadingDropdowns, setIsLoadingDropdowns] = useState(true);
  
  // Commodity type states
  const [isAutomobileHauling, setIsAutomobileHauling] = useState(false);
  const [isDryVan, setIsDryVan] = useState(false);
  const [isReefer, setIsReefer] = useState(false);
  const [isFlatbed, setIsFlatbed] = useState(false);
  const [isTanker, setIsTanker] = useState(false);
  const [fillCustomerPhones, setFillCustomerPhones] = useState(true);

  // Helper function to determine tenant ID
  const determineTenantId = (user) => {
    if (!user) return null;
    
    if (user.tenantId) {
      return user.tenantId;
    } else if (user.assignedCompanyId) {
      return user.assignedCompanyId;
    } else if (user.assignedCompanyName) {
      return `tenant_${user.assignedCompanyName.toLowerCase().replace(/\s+/g, '_')}`;
    }
    return null;
  };

  // Helper function to create tenant settings from global template
  const createTenantSettingsFromGlobal = async (tenantId, companyName) => {
    try {
      console.log("Creating tenant settings for:", tenantId);
      
      // First try globalSettings/appConfig
      let globalData = null;
      const globalSettingsRef = doc(db, "globalSettings", "appConfig");
      const globalSettingsSnap = await getDoc(globalSettingsRef);
      
      if (globalSettingsSnap.exists()) {
        globalData = globalSettingsSnap.data();
      } else {
        // Fallback to appConfig/settings if globalSettings doesn't exist
        const appConfigRef = doc(db, "appConfig", "settings");
        const appConfigSnap = await getDoc(appConfigRef);
        if (appConfigSnap.exists()) {
          globalData = appConfigSnap.data();
        }
      }

      // Create tenant settings
      const tenantSettingsRef = doc(db, "tenantSettings", tenantId);
      const newSettings = {
        ...(globalData || {}),
        tenantId,
        companyName: companyName || loggedInUser?.companyName || tenantId,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        // Ensure commodity types exist
        commodityTypes: globalData?.commodityTypes || []
      };

      await setDoc(tenantSettingsRef, newSettings);
      console.log("Successfully created tenant settings");
      return newSettings;
    } catch (error) {
      console.error("Error creating tenant settings:", error);
      throw error;
    }
  };

  useEffect(() => {
    const currentTenantId = determineTenantId(loggedInUser);
    
    if (!loggedInUser || !loggedInUser.uid || !currentTenantId) {
      console.log("Dropdown Fetch Effect: No loggedInUser or tenantId, skipping dropdown data fetch.");
      setDrivers([]);
      setTrucks([]);
      setDispatchers([]);
      setBrokers([]);
      setCompanies([]);
      setIsLoadingDropdowns(false);
      return;
    }

    console.log("Dropdown Fetch Effect: Fetching dropdown data for tenant:", currentTenantId);
    setIsLoadingDropdowns(true);

    // ============================================================================
    // COMPANY FILTERING SETUP
    // Super Admin sees everything. Other roles see only their assigned companies.
    // ============================================================================
    const userIsSuper = isSuperAdmin(loggedInUser);
    const userParentCompanyIds = loggedInUser.assignedParentCompanyIds || [];
    // One place decides how this viewer is scoped — see getViewerScope.
    const scope = getViewerScope(loggedInUser, userIsSuper);
    const hasCompanyFilter = scope.shouldFilter;
    const hasNoCompanies = scope.seesNothing;
    const grantedDriverIds = scope.grantedDriverIds;

    if (hasNoCompanies) {
      console.warn("⚠️ Non-Super Admin user has no assigned parent companies. They will see no data.");
    }

    // Store all unsubscribe functions
    const unsubscribeFunctions = [];

    // ============================================================================
    // FETCH DRIVERS
    // Scoped to every company the driver has EVER belonged to, so reassigning
    // them elsewhere doesn't strip them from their old company's dropdowns.
    // ============================================================================
    if (hasNoCompanies) {
      setDrivers([]);
    } else {
      const driversQuery = query(
        collection(db, "drivers"),
        where("tenantId", "==", currentTenantId),
        orderBy("name", "asc")
      );

      const unsubDrivers = onSnapshot(
        driversQuery,
        (snap) => {
          const all = snap.docs.map(d => ({ id: d.id, ...d.data() }));
          const visible = hasCompanyFilter
            ? all.filter(d => isDriverVisibleTo(d, userParentCompanyIds, grantedDriverIds))
            : all;

          // A dispatcher works their own drivers; admins keep the full list.
          const forDropdowns = limitToLinkedDrivers(visible, loggedInUser);

          setAllDrivers(all);
          setDrivers(forDropdowns);
          console.log(
            `✅ Drivers loaded: ${forDropdowns.length}` +
            (hasCompanyFilter ? ` of ${all.length} (company scoped)` : '') +
            (forDropdowns.length !== visible.length ? ' — limited to this dispatcher’s drivers' : '')
          );
        },
        (err) => {
          console.error("Error fetching drivers:", err);
        }
      );
      unsubscribeFunctions.push(unsubDrivers);
    }

    // ============================================================================
    // FETCH TRUCKS
    // Same pattern as drivers
    // ============================================================================
    if (hasNoCompanies) {
      setTrucks([]);
    } else {
      const trucksQuery = query(
        collection(db, "trucks"),
        where("tenantId", "==", currentTenantId),
        orderBy("unitNumber", "asc")
      );

      const unsubTrucks = onSnapshot(
        trucksQuery,
        (snap) => {
          const all = snap.docs.map(t => ({ id: t.id, ...t.data() }));
          const visible = hasCompanyFilter
            ? all.filter(t => isVisibleToCompanies(t, userParentCompanyIds))
            : all;
          setTrucks(visible);
          console.log(`✅ Trucks loaded: ${visible.length}${hasCompanyFilter ? ` of ${all.length} (company scoped)` : ''}`);
        },
        (err) => {
          console.error("Error fetching trucks:", err);
        }
      );
      unsubscribeFunctions.push(unsubTrucks);
    }

    // ============================================================================
    // FETCH DISPATCHERS
    // For non-Super Admin: filter users who share at least one parent company
    // Firestore can't do array-contains-any + array field comparison, so we
    // fetch all tenant users and filter client-side
    // ============================================================================
    const unsubDispatchers = onSnapshot(
      query(
        collection(db, "users"), 
        where("tenantId", "==", currentTenantId)
      ), 
      (snap) => {
        const allUsers = snap.docs.map(d => ({ 
          id: d.id, 
          name: d.data().name || d.data().email, 
          email: d.data().email,
          role: d.data().role,
          active: d.data().active,
          assignedParentCompanyIds: d.data().assignedParentCompanyIds || [],
          // Required by the load row, which narrows the dispatcher picker to
          // the driver's own team. It was missing here, so that filter tested
          // `Array.isArray(undefined)`, matched nobody, and left the picker
          // with no options at all — the <select> then had no option matching
          // its value and rendered blank, which looked like every load had lost
          // its dispatcher.
          //
          // Only teams of companies the user is in (getUserTeamIds): a team left
          // behind by a company move must not count anywhere.
          teamIds: getUserTeamIds(d.data())
        }));
        
        setTeamUsers(allUsers.map(u => ({ id: u.id, teamIds: u.teamIds })));
        // Everyone, for "Uploaded by" names on loads (utils/loadCreator).
        rememberPeople(snap.docs.map(d => ({ id: d.id, ...d.data() })));

        // Filter users who have "Dispatcher" role (handle both string and array)
        let dispatcherUsers = allUsers.filter(user => {
          if (Array.isArray(user.role)) {
            return user.role.includes("Dispatcher");
          }
          return user.role === "Dispatcher";
        });

        // Deactivated people are not dispatchers any more.
        //
        // They were being offered in every picker: on the largest account 38 of
        // 71 "dispatchers" had been switched off, so more than half the list was
        // people who had left. `active !== false` rather than `active === true`
        // because the field is absent on records that predate it, and those
        // accounts are working ones.
        dispatcherUsers = dispatcherUsers.filter(u => u.active !== false);
        setAllDispatchers(hasNoCompanies ? [] : dispatcherUsers);

        // ====================================================================
        // WHO THIS VIEWER MAY ASSIGN WORK TO
        // --------------------------------------------------------------------
        //   Super Admin         — every active dispatcher in the tenant.
        //   Main Admin / Admin  — the dispatchers on their own team.
        //
        // The team rule used to live on the load row and keyed off the DRIVER's
        // team, which meant a Super Admin picking for a team-assigned driver got
        // the same narrow list as everybody else, and the answer changed from
        // row to row. Keying it off the viewer instead makes the list mean one
        // thing: these are your dispatchers.
        //
        // An admin on no team falls back to their companies. 17 of 50 admins on
        // the largest account have no team, and one tenant's only admin has
        // none at all — scoping those to "their team" would hand them an empty
        // picker and no way to dispatch anything.
        // ====================================================================
        const viewerTeamIds = getUserTeamIds(loggedInUser);

        if (hasNoCompanies) {
          dispatcherUsers = [];
        } else if (!userIsSuper) {
          if (viewerTeamIds.length > 0) {
            dispatcherUsers = dispatcherUsers.filter(d =>
              (d.teamIds || []).some(t => viewerTeamIds.includes(t)));
          } else if (hasCompanyFilter) {
            dispatcherUsers = dispatcherUsers.filter(d =>
              (d.assignedParentCompanyIds || []).some(id => userParentCompanyIds.includes(id)));
          }
        }

        setDispatchers(dispatcherUsers);
        console.log(
          `✅ Dispatchers loaded: ${dispatcherUsers.length}` +
          (userIsSuper ? ' (all active)'
            : viewerTeamIds.length > 0 ? ' (your team)'
            : ' (your companies — you are on no team)')
        );
      }, 
      (err) => console.error("Error fetching dispatchers:", err)
    );
    unsubscribeFunctions.push(unsubDispatchers);

    // ============================================================================
    // FETCH BROKERS (no company filter — brokers are shared across companies)
    // ============================================================================
    const unsubBrokers = onSnapshot(
      query(
        collection(db, "brokers"), 
        where("tenantId", "==", currentTenantId),
        orderBy("name", "asc")
      ), 
      (snap) => {
        setBrokers(snap.docs.map(b => ({ id: b.id, ...b.data() })));
      }, 
      (err) => console.error("Error fetching brokers:", err)
    );
    unsubscribeFunctions.push(unsubBrokers);

    // ============================================================================
    // FETCH COMPANIES (Company Management sub-companies — used by Carrier dropdown)
    // Real-time listener, loaded once per page visit instead of re-fetched every
    // time the Add Load modal opens — avoids the slow one-off query that was
    // getting starved behind the many other listeners already open on this page.
    // ============================================================================
    const unsubCompanies = onSnapshot(
      query(
        collection(db, "companies"),
        where("tenantId", "==", currentTenantId)
      ),
      (snap) => {
        setCompanies(snap.docs.map(c => ({ id: c.id, ...c.data() })));
      },
      (err) => console.error("Error fetching companies:", err)
    );
    unsubscribeFunctions.push(unsubCompanies);

    // ============================================================================
    // FETCH TENANT SETTINGS (commodity types — shared across companies)
    // ============================================================================
    const tenantSettingsRef = doc(db, "tenantSettings", currentTenantId);
    let settingsHandled = false;
    
    const unsubSettings = onSnapshot(tenantSettingsRef, async (docSnap) => {
      if (docSnap.exists()) {
        const settingsData = docSnap.data();
        console.log("Tenant settings found:", settingsData);
        setCommodityTypes(settingsData.commodityTypes || []);
        setIsAutomobileHauling(settingsData.commodityTypes?.includes(COMMODITY_TYPES.AUTOMOBILE_HAULING) || false);
        setIsDryVan(settingsData.commodityTypes?.includes(COMMODITY_TYPES.DRY_VAN) || false);
        setIsReefer(settingsData.commodityTypes?.includes(COMMODITY_TYPES.REEFER) || false);
        setIsFlatbed(settingsData.commodityTypes?.includes(COMMODITY_TYPES.FLATBED) || false);
        setIsTanker(settingsData.commodityTypes?.includes(COMMODITY_TYPES.TANKER) || false);
        // Off only when a Super Admin turned it off (Application Settings).
        setFillCustomerPhones(settingsData.fillCustomerPhones !== false);
        settingsHandled = true;
        setIsLoadingDropdowns(false);
      } else {
        // No tenant settings exist - create them from global template
        console.warn("No tenant settings found for tenant:", currentTenantId);
        
        try {
          const newSettings = await createTenantSettingsFromGlobal(currentTenantId, loggedInUser.companyName);
          // The snapshot listener will fire again once settings are created
        } catch (error) {
          console.error("Failed to create tenant settings, using defaults:", error);
          
          setCommodityTypes([]);
          setIsAutomobileHauling(false);
          setIsDryVan(false);
          setIsReefer(false);
          setIsFlatbed(false);
          setIsTanker(false);
          settingsHandled = true;
          setIsLoadingDropdowns(false);
        }
      }
    }, (err) => {
      console.error("Error fetching tenant settings:", err);
      setCommodityTypes([]);
      setIsAutomobileHauling(false);
      setIsDryVan(false);
      setIsReefer(false);
      setIsFlatbed(false);
      setIsTanker(false);
      setIsLoadingDropdowns(false);
    });
    
    unsubscribeFunctions.push(unsubSettings);

    // Ensure loading state is set to false after a timeout (failsafe)
    const timeoutId = setTimeout(() => {
      if (!settingsHandled) {
        console.warn("Settings loading timeout - forcing completion");
        setIsLoadingDropdowns(false);
      }
    }, 5000);

    return () => {
      clearTimeout(timeoutId);
      unsubscribeFunctions.forEach(unsub => unsub());
      console.log("Dropdown Fetch Effect: Cleaned up dropdown listeners.");
    };
  }, [loggedInUser]);

  return {
    drivers,
    allDrivers,
    trucks,
    dispatchers,
    allDispatchers,
    teamUsers,
    brokers,
    companies,
    commodityTypes,
    isAutomobileHauling,
    isDryVan,
    isReefer,
    isFlatbed,
    isTanker,
    fillCustomerPhones,
    isLoadingDropdowns
  };
};